import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ZoomIn, ZoomOut, Link2, ChevronLeft, ChevronRight, ChevronUp, ChevronDown, Crosshair,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { mediaUrlForPlayback } from '@/lib/mediaUrls';
import { ExportFramingSubtitleOverlay } from '@/components/ExportFramingSubtitleOverlay';
import {
  buildExportFramingMetrics,
  captureSubtitleMediaAnchor,
  syncSubtitleLayerWithExportFraming,
} from '@/lib/exportFramingSubtitleFit';
import {
  DEFAULT_VIDEO_EXPORT_FRAMING,
  centerExportFraming,
  computeExportFramingLayout,
  exportPreviewFramePixelsForSize,
  exportTargetSizeForAspect,
  EXPORT_PREVIEW_SIZE_DEFAULT,
  normalizeVideoExportFraming,
  exportFramingPanSlop,
  stepExportFramingZoom,
  EXPORT_FRAMING_ZOOM_STEP,
} from '@/lib/videoExportFraming';

const PAN_NUDGE = 0.06;

function clamp01(n) {
  return Math.min(1, Math.max(0, Number(n) || 0));
}

/**
 * Interactive crop preview inside a fixed-size frame matching export aspect ratio.
 */
export function ExportFramingPreview({
  aspectRatio = '16:9',
  previewSize = EXPORT_PREVIEW_SIZE_DEFAULT,
  framing,
  onChange,
  mediaUrl,
  mediaType = 'video',
  subtitleStyle = null,
  subtitleLayer = null,
  subtitleText = '',
  subtitleTokens = null,
  subtitleHighlightColor = '#fde047',
  clipVideoToSubtitle = false,
  onSubtitleLayerChange,
}) {
  const previewRef = useRef(null);
  const framingRef = useRef(framing);
  const mediaSizeRef = useRef({ w: 16, h: 9 });
  const clipAnchorRef = useRef(null);
  const subtitleLayerRef = useRef(subtitleLayer);
  const [mediaSize, setMediaSize] = useState({ w: 16, h: 9 });
  const [localFraming, setLocalFraming] = useState(() => normalizeVideoExportFraming(framing));

  const framePx = useMemo(
    () => exportPreviewFramePixelsForSize(aspectRatio, previewSize),
    [aspectRatio, previewSize],
  );

  const exportTargetPx = useMemo(
    () => exportTargetSizeForAspect(aspectRatio),
    [aspectRatio],
  );

  const src = mediaUrl ? mediaUrlForPlayback(mediaUrl) : '';

  useEffect(() => {
    const next = normalizeVideoExportFraming(framing);
    framingRef.current = next;
    setLocalFraming((prev) => {
      if (
        prev.zoom === next.zoom
        && prev.panX === next.panX
        && prev.panY === next.panY
      ) {
        return prev;
      }
      return next;
    });
  }, [framing]);

  useEffect(() => {
    subtitleLayerRef.current = subtitleLayer;
  }, [subtitleLayer]);

  useEffect(() => {
    mediaSizeRef.current = mediaSize;
  }, [mediaSize]);

  useEffect(() => {
    setMediaSize({ w: 16, h: 9 });
  }, [mediaUrl]);

  const emitFraming = useCallback(
    (next) => {
      const out = normalizeVideoExportFraming(next);
      framingRef.current = out;
      setLocalFraming(out);
      onChange?.(out);

      if (clipVideoToSubtitle && clipAnchorRef.current && onSubtitleLayerChange) {
        const synced = syncSubtitleLayerWithExportFraming(
          clipAnchorRef.current,
          out,
          buildExportFramingMetrics(
            framePx.width,
            framePx.height,
            mediaSizeRef.current.w,
            mediaSizeRef.current.h,
            out,
          ),
          subtitleLayerRef.current,
        );
        subtitleLayerRef.current = synced;
        onSubtitleLayerChange(synced);
      }
    },
    [onChange, clipVideoToSubtitle, onSubtitleLayerChange, framePx.width, framePx.height],
  );

  const aspectRatioRef = useRef(aspectRatio);
  useEffect(() => {
    if (aspectRatioRef.current === aspectRatio) return;
    aspectRatioRef.current = aspectRatio;
    clipAnchorRef.current = null;
    emitFraming(centerExportFraming(framingRef.current));
  }, [aspectRatio, emitFraming]);

  const layout = useMemo(
    () => computeExportFramingLayout(
      framePx.width,
      framePx.height,
      mediaSize.w,
      mediaSize.h,
      localFraming,
    ),
    [framePx.width, framePx.height, mediaSize.w, mediaSize.h, localFraming],
  );

  const metrics = useMemo(
    () => buildExportFramingMetrics(
      framePx.width,
      framePx.height,
      mediaSize.w,
      mediaSize.h,
      localFraming,
    ),
    [framePx.width, framePx.height, mediaSize.w, mediaSize.h, localFraming],
  );

  const displaySubtitleLayer = useMemo(() => {
    if (!clipVideoToSubtitle || !clipAnchorRef.current || !subtitleLayer) {
      return subtitleLayer;
    }
    return syncSubtitleLayerWithExportFraming(
      clipAnchorRef.current,
      localFraming,
      metrics,
      subtitleLayer,
    );
  }, [clipVideoToSubtitle, subtitleLayer, localFraming, metrics]);

  const prevClipRef = useRef(false);
  useEffect(() => {
    const turnedOn = clipVideoToSubtitle && !prevClipRef.current;
    prevClipRef.current = clipVideoToSubtitle;
    if (!clipVideoToSubtitle) {
      clipAnchorRef.current = null;
      return;
    }
    if (turnedOn && subtitleLayer) {
      clipAnchorRef.current = captureSubtitleMediaAnchor(
        subtitleLayer,
        localFraming,
        metrics,
      );
    }
  }, [clipVideoToSubtitle, subtitleLayer, localFraming, metrics]);

  const panSlop = useMemo(
    () => exportFramingPanSlop(
      framePx.width,
      framePx.height,
      mediaSize.w,
      mediaSize.h,
      localFraming,
    ),
    [framePx.width, framePx.height, mediaSize.w, mediaSize.h, localFraming],
  );

  const handleZoomIn = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      const f = framingRef.current;
      emitFraming({
        ...f,
        zoom: stepExportFramingZoom(f.zoom, EXPORT_FRAMING_ZOOM_STEP),
      });
    },
    [emitFraming],
  );

  const handleZoomOut = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      const f = framingRef.current;
      emitFraming({
        ...f,
        zoom: stepExportFramingZoom(f.zoom, -EXPORT_FRAMING_ZOOM_STEP),
      });
    },
    [emitFraming],
  );

  const nudgePan = useCallback(
    (dPanX, dPanY) => {
      const f = framingRef.current;
      emitFraming({
        ...f,
        panX: clamp01(f.panX + dPanX),
        panY: clamp01(f.panY + dPanY),
      });
    },
    [emitFraming],
  );

  const handleCenter = useCallback(
    (e) => {
      e?.preventDefault?.();
      e?.stopPropagation?.();
      emitFraming(centerExportFraming(framingRef.current));
    },
    [emitFraming],
  );

  const beginPanDrag = useCallback(
    (e) => {
      if (e.button !== undefined && e.button !== 0) return;
      e.preventDefault();
      e.stopPropagation();
      const startPt = 'touches' in e ? e.touches[0] : e;
      if (!startPt) return;

      const startX = startPt.clientX;
      const startY = startPt.clientY;
      const start = { ...framingRef.current };

      const dragLay = computeExportFramingLayout(
        framePx.width,
        framePx.height,
        mediaSizeRef.current.w,
        mediaSizeRef.current.h,
        start,
      );

      const onMove = (ev) => {
        const pt = 'touches' in ev ? ev.touches[0] : ev;
        if (!pt) return;
        const dx = pt.clientX - startX;
        const dy = pt.clientY - startY;
        const panX = dragLay.maxPanX > 0
          ? clamp01(start.panX - dx / dragLay.maxPanX)
          : 0.5;
        const panY = dragLay.maxPanY > 0
          ? clamp01(start.panY - dy / dragLay.maxPanY)
          : 0.5;
        emitFraming({ ...start, panX, panY });
      };

      const onEnd = () => {
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onEnd);
        window.removeEventListener('touchmove', onMoveTh);
        window.removeEventListener('touchend', onEnd);
      };
      const onMoveTh = (ev) => {
        ev.preventDefault();
        onMove(ev);
      };
      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onEnd);
      window.addEventListener('touchmove', onMoveTh, { passive: false });
      window.addEventListener('touchend', onEnd);
    },
    [framePx.width, framePx.height, emitFraming],
  );

  useEffect(() => {
    const el = previewRef.current;
    if (!el) return undefined;
    const onWheel = (e) => {
      e.preventDefault();
      e.stopPropagation();
      const f = framingRef.current;
      emitFraming({
        ...f,
        zoom: stepExportFramingZoom(
          f.zoom,
          e.deltaY < 0 ? EXPORT_FRAMING_ZOOM_STEP : -EXPORT_FRAMING_ZOOM_STEP,
        ),
      });
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [emitFraming]);

  const norm = localFraming;
  const canZoomOut = norm.zoom > EXPORT_FRAMING_ZOOM_STEP;
  const canZoomIn = norm.zoom < 4 - EXPORT_FRAMING_ZOOM_STEP * 0.5;
  const showSubtitles = Boolean(subtitleStyle && (displaySubtitleLayer || subtitleLayer));

  return (
    <div className="space-y-2">
      <div className="flex flex-col items-center gap-2">
        <div
          ref={previewRef}
          className={`relative overflow-hidden rounded-lg border shrink-0 select-none ${
            src ? 'cursor-grab active:cursor-grabbing' : ''
          }`}
          style={{
            width: framePx.width,
            height: framePx.height,
            background: '#0a0a0a',
            borderColor: 'var(--glass-border)',
          }}
          data-testid="export-framing-preview"
          onMouseDown={src ? beginPanDrag : undefined}
          onTouchStart={src ? beginPanDrag : undefined}
        >
          {src ? (
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              <div
                className="absolute overflow-hidden"
                style={{
                  width: layout.width,
                  height: layout.height,
                  left: layout.left,
                  top: layout.top,
                }}
              >
                {mediaType === 'video' ? (
                  <video
                    key={src}
                    src={src}
                    muted
                    autoPlay
                    loop
                    playsInline
                    className="block pointer-events-none"
                    style={{
                      width: '100%',
                      height: '100%',
                      maxWidth: 'none',
                      maxHeight: 'none',
                      objectFit: 'cover',
                    }}
                    onLoadedMetadata={(ev) => {
                      const el = ev.currentTarget;
                      if (el.videoWidth > 0 && el.videoHeight > 0) {
                        setMediaSize({ w: el.videoWidth, h: el.videoHeight });
                      }
                    }}
                  />
                ) : (
                  <img
                    key={src}
                    src={src}
                    alt=""
                    draggable={false}
                    className="block pointer-events-none"
                    style={{
                      width: '100%',
                      height: '100%',
                      maxWidth: 'none',
                      maxHeight: 'none',
                      objectFit: 'cover',
                    }}
                    onLoad={(ev) => {
                      const el = ev.currentTarget;
                      if (el.naturalWidth > 0 && el.naturalHeight > 0) {
                        setMediaSize({ w: el.naturalWidth, h: el.naturalHeight });
                      }
                    }}
                  />
                )}
              </div>
            </div>
          ) : (
            <div
              className="absolute inset-0 flex items-center justify-center px-3 text-center text-[11px] leading-snug"
              style={{ color: 'var(--text-tertiary)' }}
            >
              Add a clip to the timeline to preview
            </div>
          )}
          {showSubtitles ? (
            <ExportFramingSubtitleOverlay
              frameW={framePx.width}
              frameH={framePx.height}
              layoutFrameW={exportTargetPx.width}
              layoutFrameH={exportTargetPx.height}
              aspectRatio={aspectRatio}
              subtitleStyle={subtitleStyle}
              subtitleLayer={displaySubtitleLayer || subtitleLayer}
              text={subtitleText}
              tokens={subtitleTokens}
              highlightColor={subtitleHighlightColor}
              showClipGuide={clipVideoToSubtitle}
            />
          ) : null}
          <div
            className="absolute inset-0 pointer-events-none border-2 border-white/60 rounded-lg shadow-[inset_0_0_0_1px_rgba(0,0,0,0.35)]"
            aria-hidden
          />
          {clipVideoToSubtitle && showSubtitles ? (
            <div
              className="absolute top-1.5 left-1.5 z-30 flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium pointer-events-none"
              style={{ background: 'rgba(15,23,42,0.85)', color: '#a5b4fc' }}
            >
              <Link2 className="w-3 h-3 shrink-0" aria-hidden />
              Linked
            </div>
          ) : null}
        </div>

        <div className="flex flex-wrap items-center justify-center gap-1.5 relative z-10">
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="h-8 w-8 shrink-0"
            disabled={!src || !canZoomOut}
            onClick={handleZoomOut}
            title="Zoom out"
            data-testid="export-framing-zoom-out"
          >
            <ZoomOut className="w-4 h-4" />
          </Button>
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="h-8 w-8 shrink-0"
            disabled={!src || !panSlop.canPanX || localFraming.panX <= 0.001}
            onClick={(e) => {
              e.stopPropagation();
              nudgePan(-PAN_NUDGE, 0);
            }}
            title="Pan crop left"
            data-testid="export-framing-pan-left"
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="h-8 w-8 shrink-0"
            disabled={!src}
            onClick={handleCenter}
            title="Center crop"
            data-testid="export-framing-center"
          >
            <Crosshair className="w-4 h-4" />
          </Button>
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="h-8 w-8 shrink-0"
            disabled={!src || !panSlop.canPanX || localFraming.panX >= 0.999}
            onClick={(e) => {
              e.stopPropagation();
              nudgePan(PAN_NUDGE, 0);
            }}
            title="Pan crop right"
            data-testid="export-framing-pan-right"
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="h-8 w-8 shrink-0"
            disabled={!src || !panSlop.canPanY || localFraming.panY <= 0.001}
            onClick={(e) => {
              e.stopPropagation();
              nudgePan(0, -PAN_NUDGE);
            }}
            title="Show more top"
            data-testid="export-framing-pan-up"
          >
            <ChevronUp className="w-4 h-4" />
          </Button>
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="h-8 w-8 shrink-0"
            disabled={!src || !panSlop.canPanY || localFraming.panY >= 0.999}
            onClick={(e) => {
              e.stopPropagation();
              nudgePan(0, PAN_NUDGE);
            }}
            title="Show more bottom"
            data-testid="export-framing-pan-down"
          >
            <ChevronDown className="w-4 h-4" />
          </Button>
          <span
            className="text-[11px] tabular-nums min-w-[3.25rem] text-center px-1"
            style={{ color: 'var(--text-secondary)' }}
          >
            {Math.round(norm.zoom * 100)}%
          </span>
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="h-8 w-8 shrink-0"
            disabled={!src || !canZoomIn}
            onClick={handleZoomIn}
            title="Zoom in"
            data-testid="export-framing-zoom-in"
          >
            <ZoomIn className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <p className="text-[11px] text-center" style={{ color: 'var(--text-tertiary)' }}>
        Drag to move · crosshair centers · arrows pan · scroll or +/- zoom
        {!panSlop.canPanX && src ? ' · zoom in to pan sideways' : ''}
        {clipVideoToSubtitle ? ' · captions linked to video' : ''}
      </p>
    </div>
  );
}

export function resetExportFraming() {
  return { ...DEFAULT_VIDEO_EXPORT_FRAMING };
}
